// MainForm.js
import React, { useState, useEffect, useRef  } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useAuth } from '../../AuthContext/AuthContext';
import { useNotification } from "../../SharedComponents/Notifications/NotificationContext";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Import configurations and utilities
import { INITIAL_FORM_STATE } from './config/formConfig';
import { useFormProgress } from './utils/progressTracking';
import { sanitizeForm } from '../../Utilities/DataCleanUp';
import { BASE_URL } from '../../../Constants/constants';

// Import form components
import {
    FormProgress,
    FormTabs,
    SaveButton,
} from './components/FormComponents';
import FormContent from './components/FormContent';

/**
 * MainForm Component
 * This is the primary form coordinator that manages the overall state and
 * orchestrates data flow between different form sections.
 */
const MainForm = () => {
    // Context hooks for authentication and notifications
    const { currentUser } = useAuth();
    const { showNotification } = useNotification();

    // Primary state management
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [progressKey, setProgressKey] = useState(0);
    const { totalProgress, sectionProgress, isComplete } = useFormProgress(formData, progressKey);

    const [activeTab, setActiveTab] = useState('staff_info');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const progressTriggerRef = useRef(0);




    const navigate = useNavigate();

    /**
     * Handles 404 responses by providing appropriate empty data structures
     * Different sections might need different empty states (arrays vs objects)
     */
    const handleEmptyData = (error) => {
        if (error.response?.status === 404) {
            return {
                data: error.config.url.match(/medical|training/) ? [] : {}
            };
        }
        throw error;
    };

    /**
     * Processes API errors and sets appropriate user-friendly error messages
     */
    const handleFetchError = (err) => {
        if (err.response) {
            switch (err.response.status) {
                case 404:
                    setError('Some sections have no data. You can start filling them.');
                    break;
                case 500:
                    setError('Internal server error. Please try again later.');
                    break;
                default:
                    setError('Failed to fetch data. Please try again.');
            }
        } else {
            setError('Network error. Please check your connection and try again.');
        }
    };

    /**
     * Primary data fetching function that retrieves all form sections
     * Uses Promise.all for parallel requests to optimize loading time
     */
    const fetchFormData = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const [
                staffInfoResponse,
                idocResponse,
                employmentResponse,
                contactResponse,
                equipmentResponse,
                trainingResponse,
                medicalResponse,
            ] = await Promise.all([
                axios.get(`${BASE_URL}/staff_information/me`, config),
                axios.get(`${BASE_URL}/identification_documents/me`, config),
                axios.get(`${BASE_URL}/employment/me`, config),
                axios.get(`${BASE_URL}/contact/me`, config),
                axios.get(`${BASE_URL}/equipment/me`, config),
                axios.get(`${BASE_URL}/training/me`, config),
                axios.get(`${BASE_URL}/medical/me`, config)
            ].map(request => request.catch(handleEmptyData)));

            // Update form data with fetched values, providing defaults where needed
            setFormData({
                staff_info: staffInfoResponse?.data || {},
                identification_doc: idocResponse?.data || {},
                employment: employmentResponse?.data || {},
                contact: contactResponse?.data || {
                    call_sign: '',
                    primary_emergency_contact_name: '',
                    primary_emergency_contact_number: ''
                },
                equipment: equipmentResponse?.data || {},
                training: trainingResponse?.data || [{
                    training_name: '',
                    training_status: '',
                    training_mode: '',
                    year_completed: ''
                }],
                medical: medicalResponse?.data || []
            });

            setError(null);
        } catch (err) {
            console.error('Error fetching form data:', err);
            handleFetchError(err);
        }
    };

    // Fetch initial data when component mounts
    useEffect(() => {
        fetchFormData();
    }, []);

    /**
     * Handles updates to individual form sections
     * Updates local state and triggers a refresh to ensure consistency
     */
    // const handleSectionSave = (section, values) => {
    //     console.log(`Saving section ${section} with values:`, values);
    //
    //     setFormData(prev => {
    //         const newFormData = {
    //             ...prev,
    //             [section]: values
    //         };
    //         console.log('Updated formData:', newFormData);
    //         return newFormData;
    //     });
    //
    //     // Force progress recalculation
    //     setProgressKey(prev => prev + 1);
    // };

// Replace the handleSectionSave function in MainForm.js with this version:

    /**
     * Handles updates to individual form sections
     * Updates local state and triggers a refresh to ensure consistency
     */
    /**
     * Modified handleSectionSave function to prevent the progress reset
     */
        // Updated handleSectionSave function that allows deletion
    const handleSectionSave = (section, values) => {
            console.log(`Saving section ${section} with values:`, values);

            // Add additional debugging for employment section
            if (section === 'employment') {
                console.log('EMPLOYMENT DATA BEING SAVED:', JSON.stringify(values));

                // Empty object means deletion - allow it through
                const isEmptyObject = Object.keys(values).length === 0;

                if (isEmptyObject) {
                    console.log('Empty employment object detected - THIS IS A DELETION');

                    // For deletion, we want to use a callback to ensure we have the latest state
                    setFormData(prev => {
                        const prevClone = JSON.parse(JSON.stringify(prev));

                        // For deletion, we use an empty object
                        const newFormData = {
                            ...prevClone,
                            [section]: {}  // Empty object for deletion
                        };

                        console.log(`Updated formData for ${section} deletion:`, newFormData[section]);

                        // Force progress recalculation
                        setTimeout(() => {
                            setProgressKey(prevKey => prevKey + 1);
                        }, 10);

                        return newFormData;
                    });

                    return; // Skip the rest of the function
                }

                // For non-deletion cases, check if we're trying to replace valid data with empty data
                const hasRequiredFields =
                    values.function && values.function.trim() !== '' &&
                    values.section && values.section.trim() !== '' &&
                    values.unit && values.unit.trim() !== '' &&
                    values.type_of_contract && values.type_of_contract.trim() !== '';

                const currentDataHasFields =
                    formData.employment &&
                    formData.employment.function &&
                    formData.employment.section &&
                    formData.employment.unit &&
                    formData.employment.type_of_contract;

                if (!hasRequiredFields && !isEmptyObject && currentDataHasFields) {
                    console.log('WARNING: Attempting to replace valid employment data with invalid data - PREVENTED');
                    return; // Prevent the update
                }
            }

            // Use a callback to ensure we have the latest state
            setFormData(prev => {
                // Create deep clone of previous state to avoid reference issues
                const prevClone = JSON.parse(JSON.stringify(prev));

                // Create deep clone of new values
                const valuesClone = JSON.parse(JSON.stringify(values));

                const newFormData = {
                    ...prevClone,
                    [section]: valuesClone
                };

                console.log(`Updated formData for ${section}:`, newFormData[section]);

                // Force progress recalculation after state update is complete
                setTimeout(() => {
                    setProgressKey(prevKey => prevKey + 1);
                }, 10);

                return newFormData;
            });
        };
    /**
     * Handles submission of individual form sections
     * Manages both creation and updates based on presence of ID
     */
    const handleSectionSubmit = async (endpoint, data) => {
        const token = localStorage.getItem("accessToken");
        const config = { headers: { Authorization: `Bearer ${token}` } };

        return data.id
            ? axios.put(`${BASE_URL}/${endpoint}/${data.id}`, data, config)
            : axios.post(`${BASE_URL}/${endpoint}/`, data, config);
    };

    /**
     * Special handler for array-type sections (training, medical)
     * Maps through items and submits each individually
     */
    const handleArraySubmit = async (endpoint, items) => {
        return Promise.all(
            items.map(item => handleSectionSubmit(endpoint, item))
        );
    };

    /**
     * Special handler for contact form with file upload
     * Uses FormData to handle multipart/form-data submissions
     */
    const handleContactSubmit = async (contactFormData) => {
        const token = localStorage.getItem("accessToken");
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data"
            }
        };

        const contactData = JSON.parse(contactFormData.get("contact"));
        return contactData.id
            ? axios.put(`${BASE_URL}/contact/${contactData.id}`, contactFormData, config)
            : axios.post(`${BASE_URL}/contact/`, contactFormData, config);
    };

    /**
     * Handles the final form submission
     * Validates completion status and submits all sections in parallel
     */
    const handleSubmit = async () => {
        if (!isComplete) {
            showNotification("warning", "Please complete all required fields before submitting.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("accessToken");
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // Prepare and sanitize all form data
            const sanitizedData = {
                staff_info: sanitizeForm({ ...formData.staff_info, user_id: currentUser.id }),
                identification_doc: sanitizeForm(formData.identification_doc),
                employment: sanitizeForm(formData.employment),
                contact: new FormData(),
                equipment: Array.isArray(formData.equipment)
                    ? formData.equipment.map(sanitizeForm)
                    : [sanitizeForm(formData.equipment)],
                training: formData.training.map(sanitizeForm),
                medical: formData.medical.map(sanitizeForm)
            };

            // Special handling for contact form data
            const sanitizedContact = sanitizeForm({
                ...formData.contact,
                user_id: currentUser.id
            });
            sanitizedData.contact.append("contact", JSON.stringify(sanitizedContact));
            if (sanitizedContact.proof_of_life) {
                sanitizedData.contact.append("proof_of_life", sanitizedContact.proof_of_life);
            }

            // Submit all sections in parallel
            await Promise.all([
                sanitizedData.staff_info.id
                    ? axios.put(
                        `${BASE_URL}/staff_information/${sanitizedData.staff_info.id}`,
                        sanitizedData.staff_info,
                        config
                    )
                    : axios.post(`${BASE_URL}/staff_information/`, sanitizedData.staff_info, config),
                handleSectionSubmit('identification_documents', sanitizedData.identification_doc),
                handleSectionSubmit('employment', sanitizedData.employment),
                handleContactSubmit(sanitizedData.contact),
                handleArraySubmit('equipment', sanitizedData.equipment),
                handleArraySubmit('training', sanitizedData.training),
                handleArraySubmit('medical', sanitizedData.medical)
            ]);

            showNotification("success", "Form submitted successfully!  Check IN Completed");
            setError(null);
            navigate('/dashboard')
        } catch (err) {
            console.error("Error saving form:", err);
            setError("Failed to save form. Please try again.");
            showNotification("error", "Failed to save form");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className="bg-light">
            {/* Progress Section */}
            <Row className="mb-4">
                <Col lg={{span: 8, offset: 2}} className="mt-4">
                    <FormProgress
                        totalProgress={totalProgress}
                        sectionProgress={sectionProgress}
                    />
                    {error && (
                        <div className="mt-4">
                            <div className="alert alert-danger text-center">
                                {error}
                            </div>
                        </div>
                    )}
                </Col>
            </Row>

            {/* Main Content */}
            <Row>
                <Col lg={{span: 10, offset: 1}}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Row>
                                {/* Navigation Sidebar */}
                                <Col md={3} className="border-end">
                                    <FormTabs
                                        activeTab={activeTab}
                                        setActiveTab={setActiveTab}
                                        sectionProgress={sectionProgress}
                                    />
                                    <SaveButton
                                        isComplete={isComplete}
                                        loading={loading}
                                        onSave={handleSubmit}
                                    />
                                </Col>

                                {/* Form Content */}
                                <Col md={9}>
                                    <FormContent
                                        activeTab={activeTab}
                                        formData={formData}
                                        onSave={handleSectionSave}
                                        sectionProgress={sectionProgress}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default MainForm;